import { routes } from '@/plugins/router'
import { userService } from '@/services/api'
import { cloneDeep, isEmpty } from 'lodash'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, nextTick, ref } from 'vue'
import { useSettingStore } from '../modules/setting'
import { useAlertStore } from './alert'
import { useAuthStore } from './auth'
import { type Url } from './backend'
import { useLiaStore } from './lia'
import { useNavStore } from './nav'
import { useToastStore } from './toast'
import { useResetStores } from '@/composables/useResetStores.ts'
import { useQueryClient } from '@tanstack/vue-query'
import storeConfigs from 'constants/storeConfigs.ts'
import type { Router } from 'vue-router'

type Status = 'initial' | 'loggedIn' | 'loggingIn' | 'loginFailed' | 'loggedOut'

// AAR-300/AAR-1677: logout() beendet die Sitzung mit einem Vollreload
// (window.location.href), damit von einem gesperrten Zugang garantiert kein Token
// zurückbleibt. Ein Toast, den der Aufrufer danach setzt, überlebt das nicht — beim
// deaktivierten Teammitglied ging so ausgerechnet die Meldung verloren, die erklärt,
// warum der Zugang nicht mehr funktioniert. Der Grund wird deshalb vor dem Reload
// abgelegt und von App.vue nach dem Aufbau nachgereicht.
const LOGOUT_REASON_KEY = 'logoutReason'

function stashLogoutReason(reason: unknown) {
	try {
		sessionStorage.setItem(LOGOUT_REASON_KEY, JSON.stringify(reason))
	} catch {
		// Privater Modus oder ein nicht serialisierbarer Grund darf die Abmeldung
		// nicht aufhalten — dann eben ohne Meldung.
	}
}

/**
 * Liefert den Grund der letzten erzwungenen Abmeldung genau einmal und verwirft ihn.
 */
export function takeLogoutReason() {
	try {
		const raw = sessionStorage.getItem(LOGOUT_REASON_KEY)
		if (!raw) return undefined
		sessionStorage.removeItem(LOGOUT_REASON_KEY)
		return JSON.parse(raw)
	} catch {
		return undefined
	}
}

export const useUserStore = defineStore(
	storeConfigs.user.name,
	() => {
		const authStore = useAuthStore()
		const liaStore = useLiaStore()
		const toastStore = useToastStore()
		const queryClient = useQueryClient()

		const status = ref<Status>('initial')
		const whoami = ref<Api.WhoAmI>()
		const cookieConsent = ref<boolean>()
		const permissions = ref<Api.RolePermission>()
		const auth = computed(() => authStore.auth)
		const canDelete = computed(
			() => isAdmin.value || whoami.value?.team?.can_delete === true
		)
		const canPost = computed(
			() => isAdmin.value || whoami.value?.team?.can_post === true
		)
		const canSettings = computed(
			() => isAdmin.value || whoami.value?.team?.can_settings === true
		)
		const canUpdate = computed(
			() => isAdmin.value || whoami.value?.team?.can_update === true
		)
		const isOnline = ref<boolean>(navigator.onLine)

		function db(domain: Url) {
			return whoami.value?.clientdatabases.find(
				(database) => database.subdomain === domain.subdomain
			)
		}

		const isAdmin = computed(() => whoami.value?.is_admin === true)
		const isTeam = computed(() => whoami.value?.team?.is_team === true)
		const isLoggedIn = computed(
			() =>
				status.value === 'loggedIn' &&
				token &&
				typeof whoami.value?.email === 'string'
		)
		const isLoggingIn = computed(() => status.value === 'loggingIn')
		const token = computed(() => authStore.token)
		const modules = computed(() => whoami.value?.modules)

		function loginFailure() {
			status.value = 'loginFailed'
			authStore.$reset()
		}

		function loginValues(data: {
			oauth: Api.OauthResponse
			whoami: Api.WhoAmI
		}) {
			authStore.$reset()
			whoami.value = data.whoami
		}
		function loginSuccess() {
			status.value = 'loggedIn'
		}
		function setPersons(data) {
			//	Vue.set(state, 'persons', data) //FIXME //TODO
		}

		function changePassword(input: {
			old_password: string
			new_password: string
			new_password_confirmation: string
		}) {
			return userService.changePassword(input).then(
				(data) => {
					useAlertStore().successAlert(data)
					return data
				},
				(error) => {
					return error
				}
			)
		}
		async function initialise() {
			if (auth.value === undefined || status.value !== 'initial') return

			const lia = liaStore.user

			if (!whoami.value && !lia) {
				try {
					status.value = 'loggingIn'
					whoami.value = await getWhoami()

					if (
						(!Array.isArray(whoami.value.persons) ||
							whoami.value.persons.length === 0) &&
						!whoami.value.team?.is_team
					) {
						return await logout()
					}
					status.value = 'loggedIn'
				} catch (error) {
					console.error(error)
					logout()
					return
				}
			}

			whoami.value = lia ?? whoami.value

			await useSettingStore().load(true)
			await getPermissions()
			loginSuccess()
			await useNavStore().initialise({ routes })
		}

		const loading = ref(false)

		async function liaActivate(person: Api.Person, router: Router) {
			loading.value = true
			const { initialise: initialiseNav } = useNavStore()
			liaStore.person = person
			liaStore.authenticated_user = cloneDeep(whoami.value)

			const { data } = await userService.whoami()

			whoami.value = data
			liaStore.user = data
			permissions.value = undefined
			await getPermissions()

			await initialiseNav({ routes, onlyAuthenticated: false })
			await queryClient.invalidateQueries()
			queryClient.removeQueries()
			const { resetAllItems } = useResetStores()
			resetAllItems()
			router.replace({ name: 'dashboard' })

			loading.value = false

			toastStore.toastInfo({
				is_html: true,
				message: {
					key: 'component.toast.info.actingAsUser',
					params: {
						email: data.email,
					},
				},
			})
		}
		async function liaRemove(router: Router) {
			try {
				loading.value = true
				const { initialise: initialiseNav } = useNavStore()
				whoami.value = liaStore.authenticated_user
				permissions.value = undefined
				await getPermissions()
				liaStore.$reset()

				await initialiseNav({ routes, onlyAuthenticated: false })
				await queryClient.invalidateQueries()
				queryClient.removeQueries()
				await router.replace(router.currentRoute.value)

				toastStore.toastInfo({
					message: 'component.toast.info.actingAsSelf',
				})
			} catch (error) {
			} finally {
				loading.value = false
			}
		}
		async function login({
			username,
			password,
			rememberMe,
			auth = true,
		}: Api.LoginInputInput & { auth?: boolean }) {
			useAlertStore().pendingAlert()
			status.value = 'loggingIn'

			try {
				await authStore.authenticate({ username, password, rememberMe })

				whoami.value = await getWhoami()
				await useSettingStore().load()
				await getPermissions()
				await useNavStore().initialise({ routes })
				loginSuccess()
				useAlertStore().successAlert(authStore.auth)
				return whoami.value
			} catch (error) {
				useAlertStore().errorAlert(error)
				loginFailure()
				throw error
			}
		}
		async function logout(reason?: unknown) {
			status.value = 'loggedOut'

			if (reason !== undefined) stashLogoutReason(reason)

			authStore.$reset()
			liaStore.$reset()
			useNavStore().$reset()
			$reset()

			window.location.href = '/'
		}

		function lostPassword(email: string) {
			return userService.lostPassword(email).then(
				(data) => {
					useAlertStore().successAlert(data)
					return data
				},
				(error) => {
					useAlertStore().errorAlert(error)
					return error
				}
			)
		}
		function persons({ relations = [] }) {
			/*if (typeof whoami.value?.persons !== 'undefined') { FIXME: whoami has no persons with needed relations
				return Promise.resolve(whoami.value?.persons)
			}*/

			return userService
				.persons({ relations })
				.then(({ data }) => {
					setPersons(data)
					return data
				})
				.catch((error) => {
					useAlertStore().errorAlert(error)
				})
		}
		function getPermissions() {
			if (typeof permissions.value !== 'undefined') {
				return Promise.resolve(permissions.value)
			}
			return userService
				.permissions()
				.then(({ data }) => {
					permissions.value = data
					return permissions
				})
				.catch((error) => {
					useAlertStore().errorAlert(error)
				})
		}

		async function refresh() {
			try {
				await useAuthStore().refreshAuthentication()
				status.value = 'loggedIn'
				const user = await getWhoami()
				whoami.value = user
			} catch (error) {
				useAlertStore().errorAlert(error)
				loginFailure()
			}
		}
		function resetPassword({ email, password, password_confirmation, token }) {
			// dispatch('pending', undefined, { root: true })
			return userService
				.resetPassword({ email, password, password_confirmation, token })
				.then(
					(data) => {
						useAlertStore().successAlert(data)
						return data
					},
					(error) => {
						return error
					}
				)
		}
		function getWhoami() {
			return userService
				.whoami()
				.then((data) => {
					return data.data
				})
				.catch((error) => {
					if (error.status === 403) {
						// Grund mitgeben: der Reload in logout() verwirft sonst jede
						// Meldung, die der Aufrufer danach setzt (AAR-300/AAR-1677).
						logout(error)
					}
					throw error
				})
		}

		//TODO: TJ, updateWhoami is kaputt,
		async function updateWhoami(iWhoami?: Api.WhoAmI) {
			if (iWhoami && !isEmpty(iWhoami)) {
				return new Promise((resolve) => {
					whoami.value = iWhoami
					resolve(whoami)
				})
			} else {
				return getWhoami().then(
					(data) => {
						whoami.value = data
						return data
					},
					(error) => {
						useAlertStore().errorAlert(error)
						return error
					}
				)
			}
		}

		function $reset() {
			status.value = 'initial'
			whoami.value = undefined
			permissions.value = undefined
		}

		// Cypress-only: expose a full session hydration hook so cy.login() can
		// bring the Vue app to a logged-in state after writing the auth cookie via
		// cy.setCookie(). universal-cookie's change listener never fires for CDP-
		// written cookies, so auth.value stays undefined unless we do this explicitly.
		//
		// The hook avoids calling initialise() directly because its error path
		// calls logout() → window.location.href = '/' (full page reload). Instead
		// we replicate the minimal subset of initialise() needed for the app to
		// render correctly:
		//   auth.value set → token truthy → isLoggedIn passes
		//   whoami.value.email set → email check passes
		//   settingStore loaded → Drawer.vue reads getData.documents_views safely
		//   permissions fetched → canDelete/canPost/canSettings computed correctly
		//   navStore initialised → nav drawer renders + primeNavigationDrawer() passes
		// logout() must NOT be called on failure — it reloads the page and destroys
		// the test session cookie.
		if (typeof window !== 'undefined' && (window as any).Cypress) {
			;(window as any).__cypress_hydrate_auth = async (): Promise<void> => {
				// authStore.loadFromCookie() sets auth.value from within auth.ts's
				// own scope where auth IS the ref. We cannot do authStore.auth.value =
				// from here because Pinia unwraps refs at the store boundary.
				authStore.loadFromCookie()
				status.value = 'loggingIn'
				try {
					// Call userService.whoami() directly instead of getWhoami() to
					// avoid the logout() side-effect on 403 in getWhoami()'s catch.
					const { data } = await userService.whoami()
					whoami.value = data
					// Load settings BEFORE nav init: Drawer.vue accesses
					// settingStore.getData.documents_views synchronously on mount.
					await useSettingStore().load(true)
					await getPermissions()
					await useNavStore().initialise({ routes })
					status.value = 'loggedIn'
				} catch (e) {
					// Do NOT call logout() here — it navigates to '/' and resets
					// the cookie, which would destroy the test session. Just mark
					// as loginFailed so the nav-drawer assertion in cy.login() will
					// surface the failure clearly rather than causing a page reload.
					status.value = 'loginFailed'
					console.error('[cypress_hydrate_auth] initialisation failed', e)
				}
			}
		}

		return {
			cookieConsent,
			status,
			whoami,
			permissions,
			auth,
			canDelete,
			canPost,
			canSettings,
			canUpdate,
			isAdmin,
			isTeam,
			isLoggedIn,
			isLoggingIn,
			token,
			modules,
			isOnline,
			loading,
			db,
			loginFailure,
			logout,
			loginValues,
			loginSuccess,
			setPersons,
			changePassword,
			initialise,
			liaActivate,
			liaRemove,
			login,
			lostPassword,
			persons,
			getPermissions,
			refresh,
			resetPassword,
			getWhoami,
			updateWhoami,
			$reset,
		}
	},
	{
		persist: storeConfigs.user.persist,
	}
)

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot))
}
